import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

export default function PremiumDatePicker({ attendanceDate, setAttendanceDate, fetchAttendance }) {
  const [isOpen, setIsOpen] = useState(false);

  // Helper to format date into a clean, professional reader layout (e.g., "Oct 24, 2026")
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "Select Date";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // Helper to quickly jump dates (e.g., Today, Yesterday)
  const handleQuickSelect = (daysOffset) => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + daysOffset);
    const formatted = targetDate.toISOString().split("T")[0];
    
    setAttendanceDate(formatted);
    if (fetchAttendance) fetchAttendance(formatted);
    setIsOpen(false);
  };

  const handleNativeChange = (e) => {
    const val = e.target.value;
    setAttendanceDate(val);
    if (fetchAttendance) fetchAttendance(val);
  };

  return (
    <div className="relative inline-block w-full">
      {/* Interactive Trigger Button Canvas */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between bg-white border text-xs font-bold rounded-xl pl-3.5 pr-3 py-2 outline-none transition duration-150 shadow-2xs group active:scale-[0.99] ${
          isOpen ? "border-[#002855] ring-4 ring-[#002855]/5 text-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"
        }`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <CalendarIcon className={`w-4 h-4 shrink-0 transition-colors ${
            isOpen ? "text-[#002855]" : "text-slate-400 group-hover:text-slate-600"
          }`} />
          <span className="truncate tracking-tight">{formatDisplayDate(attendanceDate)}</span>
        </div>
        <CalendarDays className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-400 transition-colors shrink-0 ml-1" />
      </button>

      {/* Floating Popover Deck Matrix */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Click Outside Overlay Layer */}
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{ type: "spring", duration: 0.25 }}
              className="absolute left-0 mt-2 w-[260px] bg-white border border-slate-200/80 rounded-2xl p-3.5 shadow-xl z-50 flex flex-col gap-3"
            >
              {/* Quick Preset Macros Sub-Deck */}
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => handleQuickSelect(0)}
                  className="py-1.5 bg-slate-50 border border-slate-200/40 hover:bg-[#002855]/5 hover:text-[#002855] text-slate-600 font-extrabold text-[10px] tracking-wider uppercase rounded-lg transition"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickSelect(-1)}
                  className="py-1.5 bg-slate-50 border border-slate-200/40 hover:bg-[#002855]/5 hover:text-[#002855] text-slate-600 font-extrabold text-[10px] tracking-wider uppercase rounded-lg transition"
                >
                  Yesterday
                </button>
              </div>

              <div className="relative border-t border-slate-100 pt-3">
                <label className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider block mb-1.5 px-0.5">
                  Pick Specific Date
                </label>
                
                {/* Custom Styled Native Box fallback hidden inside utility picker engine */}
                <div className="relative bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-xl px-3 py-2 flex items-center justify-between text-slate-700 font-bold transition">
                  <span className="text-xs">{attendanceDate || "YYYY-MM-DD"}</span>
                  <input
                    type="date"
                    value={attendanceDate}
                    onChange={handleNativeChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="flex gap-1 text-slate-400 pointer-events-none">
                    <ChevronLeft size={14} />
                    <ChevronRight size={14} />
                  </div>
                </div>
              </div>

              {/* Dynamic Status Metric Node footer */}
              <div className="flex items-center justify-between text-[10px] text-slate-400 border-t border-slate-100 pt-2.5 mt-0.5 font-medium px-0.5">
                <span>Selected:</span>
                <span className="font-mono text-slate-600 font-bold">{attendanceDate || "None"}</span>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}